import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Upload, X } from "lucide-react";
import api, { formatApiErrorDetail, imageUrl } from "../../lib/api";

const EMPTY = {
  name: "",
  model: "",
  seats: 9,
  active: true,
  maintenance: false,
  services: { rental: true, tours: true, transport: true },
  blackouts: [],
  notes: "",
  images: [],
};

export default function AdminVehicles() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null); // null | 'new' | vehicle object

  const load = () => api.get("/admin/vehicles").then((r) => setRows(r.data));

  useEffect(() => {
    load().catch(() => {});
  }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this vehicle?")) return;
    try {
      await api.delete(`/admin/vehicles/${id}`);
      toast.success("Vehicle deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-vehicles-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Vehicles</h1>
          <p className="text-sm text-slate-500">Manage your fleet and service eligibility.</p>
        </div>
        <button
          onClick={() => setEditing({ ...EMPTY })}
          data-testid="add-vehicle-button"
          className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
        >
          <Plus size={16} /> Add vehicle
        </button>
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {rows.map((v) => (
          <article key={v.id} className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid={`vehicle-row-${v.id}`}>
            <div className="aspect-[4/3] bg-slate-100 overflow-hidden">
              {v.images?.[0] ? (
                <img src={imageUrl(v.images[0])} alt={v.name} className="h-full w-full object-cover" />
              ) : (
                <div className="h-full w-full grid place-items-center text-slate-400 text-sm">No image</div>
              )}
            </div>
            <div className="p-5">
              <div className="flex items-center justify-between gap-2">
                <h3 className="font-heading font-semibold">{v.name}</h3>
                <div className="flex gap-1">
                  <button onClick={() => setEditing(v)} data-testid={`edit-vehicle-${v.id}`} className="p-1.5 rounded-md hover:bg-slate-100"><Pencil size={14} /></button>
                  <button onClick={() => remove(v.id)} data-testid={`delete-vehicle-${v.id}`} className="p-1.5 rounded-md hover:bg-red-50 text-red-600"><Trash2 size={14} /></button>
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-1">{v.model} · {v.seats} seats total</p>
              <p className="text-[11px] text-slate-400 mt-0.5">
                Self-drive: {v.seats} incl. driver · Chauffeur: {Math.max(0, v.seats - 1)} + driver
              </p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {v.active ? <Pill tone="green">Active</Pill> : <Pill tone="gray">Inactive</Pill>}
                {v.maintenance && <Pill tone="amber">Maintenance</Pill>}
                {v.services?.rental && <Pill tone="blue">Rental</Pill>}
                {v.services?.tours && <Pill tone="blue">Tours</Pill>}
                {v.services?.transport && <Pill tone="blue">Transport</Pill>}
              </div>
            </div>
          </article>
        ))}
      </div>

      {editing && <VehicleModal vehicle={editing} onClose={() => setEditing(null)} onSaved={() => { load(); setEditing(null); }} />}
    </div>
  );
}

function Pill({ tone, children }) {
  const map = {
    green: "bg-emerald-100 text-emerald-900",
    gray: "bg-slate-100 text-slate-700",
    amber: "bg-amber-100 text-amber-900",
    blue: "bg-sky-100 text-sky-900",
  };
  return <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${map[tone]}`}>{children}</span>;
}

function VehicleModal({ vehicle, onClose, onSaved }) {
  const isNew = !vehicle.id;
  const [form, setForm] = useState({ ...vehicle });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        name: form.name,
        model: form.model,
        seats: Number(form.seats) || 9,
        active: !!form.active,
        maintenance: !!form.maintenance,
        services: form.services,
        blackouts: (form.blackouts || []).filter((b) => b.start && b.end),
        notes: form.notes || null,
        images: form.images || [],
      };
      if (isNew) await api.post("/admin/vehicles", payload);
      else await api.put(`/admin/vehicles/${form.id}`, payload);
      toast.success(isNew ? "Vehicle created" : "Vehicle updated");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, images: [...(f.images || []), data.path] }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeImage = (path) => {
    setForm((f) => ({ ...f, images: f.images.filter((p) => p !== path) }));
  };

  const addBlackout = () => {
    setForm((f) => ({ ...f, blackouts: [...(f.blackouts || []), { start: "", end: "", reason: "" }] }));
  };
  const setBlackout = (i, key, val) => {
    setForm((f) => {
      const arr = [...(f.blackouts || [])];
      arr[i] = { ...arr[i], [key]: val };
      return { ...f, blackouts: arr };
    });
  };
  const rmBlackout = (i) => setForm((f) => ({ ...f, blackouts: f.blackouts.filter((_, x) => x !== i) }));

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/70 p-0 sm:p-6">
      <form
        onSubmit={save}
        data-testid="vehicle-modal-form"
        className="w-full max-w-2xl rounded-t-2xl sm:rounded-xl bg-white p-6 space-y-5 max-h-[92vh] overflow-auto"
      >
        <div className="flex items-start justify-between">
          <h3 className="font-heading text-xl font-semibold">{isNew ? "Add vehicle" : "Edit vehicle"}</h3>
          <button type="button" onClick={onClose} className="text-slate-500" aria-label="close">✕</button>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Label label="Internal name">
            <Inp required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="vehicle-name" />
          </Label>
          <Label label="Model">
            <Inp required value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} data-testid="vehicle-model" />
          </Label>
          <Label label="Seats">
            <Inp type="number" min="1" max="20" value={form.seats} onChange={(e) => setForm({ ...form, seats: e.target.value })} data-testid="vehicle-seats" />
          </Label>
          <Label label="Status">
            <div className="flex items-center gap-4 mt-2 text-sm">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={!!form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="vehicle-active" /> Active
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={!!form.maintenance} onChange={(e) => setForm({ ...form, maintenance: e.target.checked })} data-testid="vehicle-maintenance" /> Maintenance
              </label>
            </div>
          </Label>
        </div>

        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-700 mb-2">Service eligibility</p>
          <div className="flex flex-wrap gap-4 text-sm">
            {["rental", "tours", "transport"].map((s) => (
              <label key={s} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={!!form.services?.[s]}
                  data-testid={`vehicle-service-${s}`}
                  onChange={(e) => setForm({ ...form, services: { ...form.services, [s]: e.target.checked } })}
                />
                <span className="capitalize">{s}</span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-700 mb-2">Images</p>
          <div className="flex flex-wrap gap-3">
            {(form.images || []).map((p) => (
              <div key={p} className="relative">
                <img src={imageUrl(p)} alt="" className="w-24 h-24 rounded-md object-cover border border-slate-200" />
                <button type="button" onClick={() => removeImage(p)} className="absolute -top-2 -right-2 bg-white rounded-full border border-slate-200 p-0.5" aria-label="remove image">
                  <X size={14} />
                </button>
              </div>
            ))}
            <label className="w-24 h-24 rounded-md border-2 border-dashed border-slate-300 grid place-items-center text-slate-500 text-xs cursor-pointer hover:bg-slate-50">
              <input ref={fileRef} type="file" accept="image/*" onChange={onUpload} className="hidden" data-testid="vehicle-image-upload" />
              {uploading ? "Uploading…" : <><Upload size={16} className="mb-1" /> Upload</>}
            </label>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-700">Blackout dates</p>
            <button type="button" onClick={addBlackout} className="text-xs font-semibold text-slate-700 hover:text-slate-900" data-testid="add-blackout">+ Add</button>
          </div>
          <div className="space-y-2">
            {(form.blackouts || []).map((b, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-center">
                <Inp type="datetime-local" value={b.start?.slice(0, 16) || ""} onChange={(e) => setBlackout(i, "start", e.target.value ? new Date(e.target.value).toISOString() : "")} />
                <Inp type="datetime-local" value={b.end?.slice(0, 16) || ""} onChange={(e) => setBlackout(i, "end", e.target.value ? new Date(e.target.value).toISOString() : "")} />
                <Inp placeholder="Reason" value={b.reason || ""} onChange={(e) => setBlackout(i, "reason", e.target.value)} />
                <button type="button" onClick={() => rmBlackout(i)} className="text-red-600" aria-label="remove"><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        </div>

        <Label label="Notes">
          <textarea rows={3} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="vehicle-notes" className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-slate-900 outline-none" />
        </Label>

        <div className="flex items-center justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
          <button type="submit" disabled={busy} data-testid="vehicle-save" className="rounded-md bg-slate-900 px-5 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60">
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Label({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-wider text-slate-700">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function Inp(props) {
  return (
    <input
      {...props}
      className={`w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-slate-900 outline-none ${props.className || ""}`}
    />
  );
}
